import React, { useCallback, useMemo, useRef } from 'react';
import { Select } from 'antd';
import type { SelectProps } from 'antd';
import { useAppDispatch, useAppSelector, useAuth } from '../../store/hooks';
import { loadCompaniesInitial, loadCompaniesNext } from '../../store/adminSlice';

type AdminCompanySelectProps = SelectProps;

const AdminCompanySelect: React.FC<AdminCompanySelectProps> = (props) => {
  const dispatch = useAppDispatch();
  const { claims } = useAuth();
  const companies = useAppSelector(s => s.admin.companies.items);
  const cursor = useAppSelector(s => s.admin.companies.cursor);
  const loading = useAppSelector(s => s.admin.companies.loading);
  const loadAttempted = useRef(false);

  const options = useMemo(() => {
    return companies.map((c: any) => ({
      value: c?.id,
      label: c?.name || c?.id,
    }));
  }, [companies]);

  const loadIfNeeded = useCallback(() => {
    if (!claims?.superadmin) return;
    if (companies.length > 0) return;
    if (loading) return;
    if (loadAttempted.current) return;
    loadAttempted.current = true;
    dispatch(loadCompaniesInitial(50));
  }, [claims?.superadmin, companies.length, dispatch, loading]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (loading || !cursor) return;
    const target = e.currentTarget;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 40) {
      dispatch(loadCompaniesNext({ pageSize: 50, cursor }));
    }
  }, [cursor, dispatch, loading]);

  const { loading: loadingProp, options: optionsProp, onDropdownVisibleChange, onPopupScroll, ...rest } = props;

  return (
    <Select
      showSearch
      placeholder="Select a company"
      optionFilterProp="label"
      options={optionsProp ?? options}
      loading={loadingProp ?? loading}
      filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
      onDropdownVisibleChange={(open) => {
        if (open) loadIfNeeded();
        onDropdownVisibleChange?.(open);
      }}
      onPopupScroll={(e) => {
        handleScroll(e);
        onPopupScroll?.(e);
      }}
      {...rest}
    />
  );
};

export default AdminCompanySelect;
